import { useState } from 'react';

import { Input } from '@iwoca/orion';

import styles from './NumberField.module.css';
import { parseIntDe } from '../logic/parseIntDe';

type TProps = {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  onBlur?: () => void;
  placeholder?: string;
  suffix?: string;
  error?: string;
  min?: number;
  max?: number;
  rangeError?: string;
  ariaLabel: string;
};

export const NumberField = ({
  value,
  onChange,
  onBlur,
  placeholder,
  suffix,
  error,
  min,
  max,
  rangeError,
  ariaLabel,
}: TProps) => {
  // Whole numbers within [min, max] resolve to that number; anything else is unscored.
  const resolve = (parsed: number | undefined): number | undefined => {
    if (parsed === undefined) return undefined;
    if (min !== undefined && parsed < min) return undefined;
    if (max !== undefined && parsed > max) return undefined;
    return parsed;
  };

  // Internal raw string so German grouping dots accumulate while typing.
  const [rawValue, setRawValue] = useState<string>(
    value !== undefined ? String(value) : '',
  );
  const [rangeErrorShown, setRangeErrorShown] = useState(false);
  const [lastValue, setLastValue] = useState(value);

  // Resync the visible input when the value prop changes externally (e.g. reset),
  // but leave the raw text untouched while it still resolves to the current value.
  if (value !== lastValue) {
    setLastValue(value);
    if (resolve(parseIntDe(rawValue)) !== value) {
      setRawValue(value !== undefined ? String(value) : '');
      setRangeErrorShown(false);
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Restrict to digits and German grouping separators — letters never reach the field.
    const digitsOnly = event.target.value.replace(/[^\d.\s]/g, '');
    setRawValue(digitsOnly);
    const parsed = parseIntDe(digitsOnly);
    const resolved = resolve(parsed);
    setRangeErrorShown(parsed !== undefined && resolved === undefined);
    onChange(resolved);
  };

  return (
    <Input
      type="text"
      inputMode="numeric"
      aria-label={ariaLabel}
      value={rawValue}
      onChange={handleChange}
      onBlur={onBlur}
      placeholder={placeholder}
      error={error ?? (rangeErrorShown ? rangeError : undefined)}
      iconRight={
        suffix ? <span className={styles.suffix}>{suffix}</span> : undefined
      }
    />
  );
};
